import React, { useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { parseString } from "xml2js";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import LandingPage from "./LandingPage";

// MSAL Configuration
const msalConfig = {
  auth: {
    clientId: "1c5f5f39-dcc4-4d6d-aa48-0af4388122e5",
    authority: "https://login.microsoftonline.com/0c329fd7-b7e2-4ccd-9cff-e8de618a0d90",
    redirectUri: "https://azcertstatus.keywordsstudios.com",
  },
};

const loginRequest = {
  scopes: ["User.Read", "Application.Read.All", "Directory.Read.All"],
};

const msalInstance = new PublicClientApplication(msalConfig);

// Certificate Details Component
const CertificateDetails = ({
  appData,
  loading,
  error,
  filterType,
  filterStatus,
  filterSource,
  setFilterType,
  setFilterStatus,
  setFilterSource,
  onLogout,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("contains");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const rowsPerPage = 10;

  const getStatus = (expiryDate) => {
    if (!expiryDate || expiryDate === "N/A") return "No Expiry";
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    if (diffDays < 0) return "Expired";
    if (diffDays <= 30) return "Expiring Soon";
    return "Valid";
  };

  const filteredData = appData.filter((item) => {
    const matchType = filterType === "-- All --" || item.certType === filterType;
    const matchStatus = filterStatus === "-- All --" || getStatus(item.expiryDate) === filterStatus;
    const matchSource = filterSource === "-- All --" || item.source === filterSource;

    const appNameLower = item.appName.toLowerCase();
    const queryLower = searchQuery.toLowerCase();
    const matchSearch =
      searchMode === "startsWith"
        ? appNameLower.startsWith(queryLower)
        : appNameLower.includes(queryLower);

    return matchType && matchStatus && matchSource && (searchQuery === "" || matchSearch);
  });

  const expiringSoonData = filteredData.filter(item => getStatus(item.expiryDate) === "Expiring Soon");

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const uniqueTypes = ["-- All --", ...new Set(appData.map((i) => i.certType))];
  const uniqueStatuses = ["-- All --", "Valid", "Expiring Soon", "Expired", "No Expiry"];
  const uniqueSources = ["-- All --", ...new Set(appData.map((i) => i.source))];

  const sendEmailNotification = async () => {
    if (!recipientEmail) {
      setNotificationStatus("Please enter a recipient email address.");
      return;
    }

    try {
      setNotificationStatus("Sending email notification...");
      const response = await fetch("https://z8u21dpbxb.execute-api.eu-west-1.amazonaws.com/Prod/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          certificates: expiringSoonData,
          recipientEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send email notification");
      }
      setNotificationStatus("Email notification sent successfully!");
    } catch (err) {
      console.error("Error sending email notification:", err);
      setNotificationStatus("Failed to send email notification.");
    }
  };

  const styles = `
    @keyframes blink {
      0% { opacity: 1; }
      50% { opacity: 0; }
      100% { opacity: 1; }
    }
    .blink {
      animation: blink 1s infinite;
      margin-left: 8px;
      vertical-align: middle;
    }
    .pagination {
      margin-top: 20px;
      display: flex;
      justify-content: center;
      gap: 10px;
    }
    .pagination button {
      padding: 5px 10px;
      background-color: #0078d4;
      color: white;
      border: none;
      border-radius: 3px;
      cursor: pointer;
    }
    .pagination button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
    .footer-logo {
      display: flex;
      justify-content: flex-start;
      margin-top: 30px;
      margin-bottom: 20px;
    }
    .loading-container {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .progress-boxes {
      display: flex;
      gap: 4px;
      align-items: center;
    }
    .progress-box {
      width: 16px;
      height: 8px;
      background-color: #cccccc;
      border-radius: 2px;
      animation: fill 1.5s ease-in-out infinite;
    }
    .progress-box:nth-child(1) { animation-delay: 0s; }
    .progress-box:nth-child(2) { animation-delay: 0.3s; }
    .progress-box:nth-child(3) { animation-delay: 0.6s; }
    .progress-box:nth-child(4) { animation-delay: 0.9s; }
    .progress-box:nth-child(5) { animation-delay: 1.2s; }
    @keyframes fill {
      0% { background-color: #cccccc; }
      50% { background-color: #0078d4; }
      100% { background-color: #cccccc; }
    }
    .search-container {
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .search-input {
      padding: 5px;
      font-size: 14px;
      border: 1px solid #ddd;
      border-radius: 3px;
    }
    .notification-status {
      margin-top: 10px;
      font-size: 14px;
      color: #d83b01;
    }
    .email-button {
      padding: 5px 10px;
      background-color: #28a745;
      color: white;
      border: none;
      border-radius: 3px;
      cursor: pointer;
      margin-left: 10px;
    }
    .email-input {
      padding: 5px;
      font-size: 14px;
      border: 1px solid #ddd;
      border-radius: 3px;
      width: 200px;
    }
  `;

  return (
    <div style={{ padding: 30, fontFamily: "Arial, sans-serif" }}>
      <style>{styles}</style>

      <div style={{ marginBottom: 20, display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={onLogout}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 16px",
            fontSize: 14,
            backgroundColor: "#d83b01",
            color: "white",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
            gap: 8,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 4H4V20H10M16 8L20 12M16 16L20 12M20 12H10"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Logout
        </button>
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}

      {loading ? (
        <div className="loading-container">
          <div className="progress-boxes">
            <div className="progress-box"></div>
            <div className="progress-box"></div>
            <div className="progress-box"></div>
            <div className="progress-box"></div>
            <div className="progress-box"></div>
          </div>
          <p>Loading certificates... (this may take a moment)</p>
        </div>
      ) : (
        <>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by Application Name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <select
              value={searchMode}
              onChange={(e) => {
                setSearchMode(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="contains">Contains</option>
              <option value="startsWith">Starts With</option>
            </select>
            <input
              type="email"
              className="email-input"
              placeholder="Enter recipient email..."
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
            <button
              className="email-button"
              onClick={sendEmailNotification}
              disabled={expiringSoonData.length === 0 || !recipientEmail}
            >
              Send Expiring Soon Email
            </button>
          </div>
          {notificationStatus && (
            <p className="notification-status">{notificationStatus}</p>
          )}

          <div style={{ marginBottom: 10 }}>
            Filter by Certificate Type:{" "}
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
            >
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {"  "} Status:{" "}
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            {"  "} Source:{" "}
            <select
              value={filterSource}
              onChange={(e) => {
                setFilterSource(e.target.value);
                setCurrentPage(1);
              }}
            >
              {uniqueSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>

          {filteredData.length === 0 ? (
            <p>No credentials found.</p>
          ) : (
            <>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 20,
                }}
                role="grid"
              >
                <thead>
                  <tr style={{ backgroundColor: "#0078d4", color: "white" }}>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>
                      Application Name
                    </th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>
                      Certificate Name
                    </th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>
                      Certificate Type
                    </th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>
                      Expiry Date
                    </th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>
                      Thumbprint
                    </th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>Status</th>
                    <th style={{ padding: 10, border: "1px solid #ddd" }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, idx) => {
                    const status = getStatus(item.expiryDate);
                    let statusColor =
                      status === "Expired"
                        ? "red"
                        : status === "Expiring Soon"
                        ? "orange"
                        : "green";

                    return (
                      <tr key={idx}>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.appName}
                        </td>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.certName}
                        </td>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.certType}
                        </td>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.expiryDate !== "N/A"
                            ? new Date(item.expiryDate).toLocaleString()
                            : "N/A"}
                        </td>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.thumbprint}
                        </td>
                        <td
                          style={{ padding: 10, border: "1px solid #ddd", color: statusColor, fontWeight: "bold" }}
                        >
                          {status}
                          {status === "Expiring Soon" && (
                            <span className="blink">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ verticalAlign: "middle" }}
                                role="img"
                                aria-label="alert"
                              >
                                <path
                                  d="M12 2L2 22H22L12 2ZM12 8V14M12 16V18"
                                  fill="#ff0000"
                                  stroke="#ff0000"
                                  strokeWidth="2"
                                />
                              </svg>
                            </span>
                          )}
                        </td>
                        <td style={{ padding: 10, border: "1px solid #ddd" }}>
                          {item.source}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span style={{ padding: "5px 10px" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>

              <div className="footer-logo">
                <img
                  src="/logo.png"
                  alt="Company Logo"
                  style={{ width: 150, height: "auto" }}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// Main App Component with Routing
function App() {
  const [account, setAccount] = useState(null);
  const [token, setToken] = useState(null);
  const [appData, setAppData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filterType, setFilterType] = useState("-- All --");
  const [filterStatus, setFilterStatus] = useState("-- All --");
  const [filterSource, setFilterSource] = useState("-- All --");

  const calculateThumbprint = (cert) => {
    try {
      const certData = cert.replace(/-----BEGIN CERTIFICATE-----|-----END CERTIFICATE-----|\n|\r/g, '');
      const binaryDer = atob(certData);
      const buffer = new Uint8Array(binaryDer.length);
      for (let i = 0; i < binaryDer.length; i++) {
        buffer[i] = binaryDer.charCodeAt(i);
      }
      return crypto.subtle.digest("SHA-1", buffer).then(hash => {
        const hashArray = Array.from(new Uint8Array(hash));
        const thumbprint = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
        return thumbprint;
      });
    } catch (error) {
      console.error("Error calculating thumbprint:", error);
      return "N/A";
    }
  };

  const fetchSamlMetadata = async (metadataUrl) => {
    try {
      const response = await fetch(metadataUrl);
      if (!response.ok) throw new Error("Failed to fetch SAML metadata");
      const xmlText = await response.text();

      return new Promise((resolve, reject) => {
        parseString(xmlText, (err, result) => {
          if (err) {
            reject(err);
            return;
          }

          try {
            const keyDescriptor = result?.EntityDescriptor?.IDPSSODescriptor?.[0]?.KeyDescriptor?.find(
              kd => kd.$.use === "signing"
            );
            const cert = keyDescriptor?.KeyInfo?.[0]?.['X509Data']?.[0]?.['X509Certificate']?.[0];
            const expiryDate = keyDescriptor?.KeyInfo?.[0]?.['X509Data']?.[0]?.['X509Certificate']?.[0]?.['NotAfter']?.[0] || "N/A";

            if (!cert) {
              resolve({ expiryDate: "N/A", thumbprint: "N/A" });
              return;
            }

            calculateThumbprint(cert).then(thumbprint => {
              resolve({
                expiryDate: expiryDate || "N/A",
                thumbprint: thumbprint || "N/A",
              });
            });
          } catch (error) {
            console.error("Error parsing SAML metadata:", error);
            resolve({ expiryDate: "N/A", thumbprint: "N/A" });
          }
        });
      });
    } catch (error) {
      console.error("Error fetching SAML metadata:", error);
      return { expiryDate: "N/A", thumbprint: "N/A" };
    }
  };

  const mapAppCreds = (apps) =>
    apps.flatMap((app) => {
      const creds = [];
      app.keyCredentials?.forEach((kc) =>
        creds.push({
          appName: app.displayName || "Unnamed App",
          certName: kc.displayName || "Key Credential",
          certType: "Certificate",
          expiryDate: kc.endDateTime,
          thumbprint: "N/A",
          source: "App Registration",
        })
      );
      app.passwordCredentials?.forEach((pc) =>
        creds.push({
          appName: app.displayName || "Unnamed App",
          certName: pc.displayName || "Password Credential",
          certType: "Secret",
          expiryDate: pc.endDateTime,
          thumbprint: "N/A",
          source: "App Registration",
        })
      );
      return creds;
    });

  const mapSPCreds = async (sps) => {
    console.log("Fetched Service Principals:", sps.map(sp => ({
      displayName: sp.displayName,
      preferredSingleSignOnMode: sp.preferredSingleSignOnMode,
      samlMetadataUrl: sp.samlMetadataUrl,
      servicePrincipalNames: sp.servicePrincipalNames,
      passwordCredentials: sp.passwordCredentials,
    })));

    const credsPromises = sps.map(async (sp) => {
      const creds = [];
      const isSaml = sp.preferredSingleSignOnMode === "saml" || 
                    sp.samlMetadataUrl || 
                    (sp.displayName && (sp.displayName.includes("SAML") || sp.displayName.includes("ServiceNow"))) || 
                    (sp.servicePrincipalNames && sp.servicePrincipalNames.some(name => name.includes("SAML")));

      if (isSaml && sp.samlMetadataUrl) {
        const { expiryDate, thumbprint } = await fetchSamlMetadata(sp.samlMetadataUrl);
        creds.push({
          appName: sp.displayName || "Unnamed Enterprise App",
          certName: "CN=Microsoft Azure Federated SSO Certificate",
          certType: "SAML",
          expiryDate,
          thumbprint,
          source: "Enterprise Application",
        });
      }

      sp.passwordCredentials?.forEach((pc) => {
        creds.push({
          appName: sp.displayName || "Unnamed Enterprise App",
          certName: pc.displayName || "Password Credential",
          certType: "Secret",
          expiryDate: pc.endDateTime,
          thumbprint: "N/A",
          source: "Enterprise Application",
        });
      });

      return creds;
    });

    return (await Promise.all(credsPromises)).flat();
  };

  const signIn = async (navigate) => {
    try {
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      if (loginResponse.account) {
        setAccount(loginResponse.account);
        msalInstance.setActiveAccount(loginResponse.account);
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: loginResponse.account,
        });
        setToken(tokenResponse.accessToken);
        navigate("/certificates");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Failed to sign in.");
      setAccount(null);
      setToken(null);
      msalInstance.clearCache();
      msalInstance.setActiveAccount(null);
    }
  };

  const signOut = async (navigate) => {
    try {
      await msalInstance.initialize();
      msalInstance.clearCache();
      msalInstance.setActiveAccount(null);
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: "https://azcertstatus.keywordsstudios.com",
      });
      setAccount(null);
      setToken(null);
      setAppData([]);
      setError(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      setAccount(null);
      setToken(null);
      setAppData([]);
      setError(null);
      msalInstance.clearCache();
      msalInstance.setActiveAccount(null);
      navigate("/");
    }
  };

  const fetchData = async (accessToken) => {
    setLoading(true);
    setError(null);
    try {
      const fetchAllPages = async (initialUrl) => {
        let allData = [];
        let nextLink = initialUrl;
        const requests = [];

        while (nextLink) {
          requests.push(
            fetch(nextLink, {
              headers: { Authorization: `Bearer ${accessToken}` },
            }).then(res => {
              if (!res.ok) throw new Error(`Failed to fetch: ${nextLink}`);
              return res.json();
            })
          );
          const firstPage = await requests[requests.length - 1];
          allData = [...allData, ...(firstPage.value || [])];
          nextLink = firstPage["@odata.nextLink"];
        }

        const responses = await Promise.all(requests);
        return responses.flatMap(response => response.value || []);
      };

      const [appData, spData] = await Promise.all([
        fetchAllPages("https://graph.microsoft.com/v1.0/applications?$top=100"),
        fetchAllPages("https://graph.microsoft.com/v1.0/servicePrincipals?$top=100"),
      ]);

      const appCreds = mapAppCreds(appData);
      const spCreds = await mapSPCreds(spData);

      setAppData([...appCreds, ...spCreds]);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data.");
      setAppData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      fetchData(token);
    }
  }, [token]);

  const AppWrapper = () => {
    const navigate = useNavigate();

    return (
      <Routes>
        <Route
          path="/"
          element={<LandingPage account={account} onLogin={() => signIn(navigate)} onLogout={() => signOut(navigate)} />}
        />
        <Route
          path="/certificates"
          element={
            account ? (
              <CertificateDetails
                appData={appData}
                loading={loading}
                error={error}
                filterType={filterType}
                filterStatus={filterStatus}
                filterSource={filterSource}
                setFilterType={setFilterType}
                setFilterStatus={setFilterStatus}
                setFilterSource={setFilterSource}
                onLogout={() => signOut(navigate)}
              />
            ) : (
              <LandingPage account={account} onLogin={() => signIn(navigate)} onLogout={() => signOut(navigate)} />
            )
          }
        />
      </Routes>
    );
  };

  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}

export default App;